import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  ClipboardCheck, 
  FileBarChart, 
  CreditCard, 
  TrendingUp,
  LogOut,
  Menu,
  X,
  BookOpen,
  ScrollText,
  Settings,
  UserCheck,
  BarChart3,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/attendance", icon: ClipboardCheck, label: "Attendance" },
  { path: "/private-lessons", icon: UserCheck, label: "Private Lessons" },
  { path: "/payments", icon: CreditCard, label: "Payments" },
  { path: "/students", icon: Users, label: "Students" },
  { path: "/grading", icon: Award, label: "Grading" },
  { path: "/sifu-ledger", icon: ScrollText, label: "Master Log" },
  { path: "/forms-library", icon: BookOpen, label: "Forms Library" },
  { path: "/attendance-reports", icon: FileBarChart, label: "Attendance Reports" },
  { path: "/payment-reports", icon: TrendingUp, label: "Payment Reports" },
  { path: "/billing-reports", icon: BarChart3, label: "Billing & Reports" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout({ children, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on ANY route change (mobile) - ensures sidebar always closes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogoClick = () => {
    navigate('/');
    setSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#18181b] rounded-sm border border-white/10"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#18181b] border-r border-white/10 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo - Clickable to navigate to Dashboard */}
          <div className="p-4 lg:p-6 border-b border-white/5">
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-2 sm:gap-3 pl-10 lg:pl-0 w-full hover:opacity-80 transition-opacity cursor-pointer"
              data-testid="logo-link"
            >
              <img 
                src="/assets/logo.png" 
                alt="Loong Fu Logo" 
                className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 object-contain flex-shrink-0"
                data-testid="school-logo"
              />
              <div className="min-w-0 text-left">
                <h1 className="text-base sm:text-lg lg:text-xl font-bold tracking-wider text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  LOONG FU
                </h1>
                <p className="text-[10px] sm:text-xs text-[#D4AF37] tracking-widest" style={{ fontFamily: 'Oswald, sans-serif' }}>龍 虎 堂</p>
              </div>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                    isActive
                      ? "bg-[#27272a] border-l-2 border-[#D4AF37] text-white"
                      : "text-[#A1A1AA] hover:bg-[#27272a] hover:text-white"
                  }`
                }
              >
                <item.icon size={20} />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <Button
              data-testid="logout-btn"
              variant="ghost"
              className="w-full justify-start gap-3 text-[#A1A1AA] hover:text-white hover:bg-[#27272a]"
              onClick={onLogout}
            >
              <LogOut size={20} />
              <span className="text-sm font-medium">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-0 min-h-screen overflow-auto">
        <div className="pt-16 lg:pt-0 p-3 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
